const STORAGE_KEYS = {
  jwt: "jwt",
  refreshToken: "r_token",
  email: "email",
  role: "role",
  name: "name",
  id: "id",
  isSuperadmin: "is_superadmin",
  permissions: "permissions",
};

const AUTH_REFRESH_INTERVAL_MS = 30000;
let authRefreshPromise = null;
let lastAuthRefreshAt = 0;

function normalizePermissionValue(value) {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || null;
  }

  if (value && typeof value === "object") {
    if (typeof value.codename === "string") {
      const codename = value.codename.trim();
      return codename || null;
    }
  }

  return null;
}

export function normalizePermissions(input) {
  if (!Array.isArray(input)) {
    return [];
  }

  const unique = new Set();
  input.forEach((entry) => {
    const normalized = normalizePermissionValue(entry);
    if (normalized) {
      unique.add(normalized);
    }
  });

  return Array.from(unique);
}

export function safeParsePermissions(rawValue) {
  try {
    const parsed = JSON.parse(rawValue || "[]");
    return normalizePermissions(parsed);
  } catch {
    return [];
  }
}

export function isAuthenticated() {
  return Boolean(localStorage.getItem(STORAGE_KEYS.email) && localStorage.getItem(STORAGE_KEYS.jwt));
}

export function isSuperadmin() {
  return localStorage.getItem(STORAGE_KEYS.isSuperadmin) === "true";
}

export function getPermissions() {
  return safeParsePermissions(localStorage.getItem(STORAGE_KEYS.permissions));
}

export function can(permission) {
  if (isSuperadmin()) {
    return true;
  }

  if (!permission) {
    return false;
  }

  return getPermissions().includes(permission);
}

export function canAny(permissionList = []) {
  if (isSuperadmin()) {
    return true;
  }

  if (!Array.isArray(permissionList) || permissionList.length === 0) {
    return false;
  }

  const userPermissions = getPermissions();
  return permissionList.some((perm) => userPermissions.includes(perm));
}

export function canAll(permissionList = []) {
  if (isSuperadmin()) {
    return true;
  }

  if (!Array.isArray(permissionList) || permissionList.length === 0) {
    return true;
  }

  const userPermissions = getPermissions();
  return permissionList.every((perm) => userPermissions.includes(perm));
}

export function hydrateAuthStateFromStorage() {
  const normalizedPermissions = safeParsePermissions(localStorage.getItem(STORAGE_KEYS.permissions));
  localStorage.setItem(STORAGE_KEYS.permissions, JSON.stringify(normalizedPermissions));

  const normalizedSuperadmin = localStorage.getItem(STORAGE_KEYS.isSuperadmin) === "true";
  localStorage.setItem(STORAGE_KEYS.isSuperadmin, String(normalizedSuperadmin));
}

export function setAuthSession(payload = {}) {
  const normalizedPermissions = normalizePermissions(payload.permissions);

  localStorage.setItem(STORAGE_KEYS.jwt, payload.access_token || "");
  localStorage.setItem(STORAGE_KEYS.refreshToken, payload.r_token || "");
  localStorage.setItem(STORAGE_KEYS.email, payload.email || "");
  localStorage.setItem(STORAGE_KEYS.role, payload.role || "");
  localStorage.setItem(STORAGE_KEYS.name, payload.name || "");
  localStorage.setItem(STORAGE_KEYS.id, payload.id != null ? String(payload.id) : "");
  localStorage.setItem(STORAGE_KEYS.isSuperadmin, String(payload.is_superadmin === true));
  localStorage.setItem(STORAGE_KEYS.permissions, JSON.stringify(normalizedPermissions));

  return normalizedPermissions;
}

export async function refreshAuthSessionFromServer({ force = false } = {}) {
  if (!isAuthenticated()) {
    return null;
  }

  const now = Date.now();
  if (!force && now - lastAuthRefreshAt < AUTH_REFRESH_INTERVAL_MS) {
    return null;
  }

  if (authRefreshPromise) {
    return authRefreshPromise;
  }

  authRefreshPromise = (async () => {
    try {
      const { default: api } = await import("../../boot/axios.js");
      const res = await api.get("/auth/currentuser");
      const user = res?.data || {};

      if (Array.isArray(user.permissions)) {
        localStorage.setItem(STORAGE_KEYS.permissions, JSON.stringify(normalizePermissions(user.permissions)));
      }

      if (typeof user.is_superadmin === "boolean") {
        localStorage.setItem(STORAGE_KEYS.isSuperadmin, String(user.is_superadmin));
      }

      if (user.role != null) {
        localStorage.setItem(STORAGE_KEYS.role, String(user.role));
      }

      if (user.email != null) {
        localStorage.setItem(STORAGE_KEYS.email, String(user.email));
      }

      if (user.id != null) {
        localStorage.setItem(STORAGE_KEYS.id, String(user.id));
      }

      const displayName =
        [user.first_name, user.last_name].filter(Boolean).join(" ").trim() ||
        user.name ||
        user.email ||
        localStorage.getItem(STORAGE_KEYS.name) ||
        "";

      if (displayName) {
        localStorage.setItem(STORAGE_KEYS.name, displayName);
      }

      lastAuthRefreshAt = Date.now();

      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("auth-session-updated"));
      }

      return user;
    } catch {
      return null;
    } finally {
      authRefreshPromise = null;
    }
  })();

  return authRefreshPromise;
}
